import * as React from 'react';

import { Button } from '@/components/TailwindComponents/base/Button';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { TailwindStyles } from '@/components/TailwindComponents/styles';

export interface TwButtonGroupOption {
  value: string;
  label: string;
}

export interface TwButtonGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'style'> {
  options: TwButtonGroupOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
}

const TwButtonGroup = React.forwardRef<HTMLDivElement, TwButtonGroupProps>(
  (
    {
      options,
      value: controlledValue,
      defaultValue,
      onChange,
      className,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || options[0]?.value || '',
    );

    const isControlled = controlledValue !== undefined;
    const currentValue = isControlled ? controlledValue : internalValue;

    const handleChange = (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    };

    return (
      <div
        ref={ref}
        role="group"
        className={cn(
          'inline-flex h-9 rounded-md border border-[#CFD6DA] dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-gray-100',
          className,
        )}
        {...(props as any)}
      >
        {options.map((option, index) => {
          const isSelected = currentValue === option.value;
          const isFirst = index === 0;
          const isLast = index === options.length - 1;

          return (
            <Button
              key={option.value}
              type="button"
              variant={isSelected ? 'default' : 'outline'}
              className={cn(
                'h-full flex-1 rounded-none border-0 px-4 py-2 text-sm font-medium transition-colors',
                isFirst && 'rounded-l-md',
                isLast && 'rounded-r-md',
                isSelected
                  ? 'text-white hover:opacity-90'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700',
                // Remove border between buttons
                !isFirst && '-ml-[1px]',
              )}
              style={
                isSelected
                  ? {
                      backgroundColor: TailwindStyles.colors.Red.primary,
                    }
                  : undefined
              }
              onClick={() => handleChange(option.value)}
            >
              {option.label}
            </Button>
          );
        })}
      </div>
    );
  },
);

TwButtonGroup.displayName = 'TwButtonGroup';

export { TwButtonGroup };
